import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  changeCourierRequest,
  deleteProofOfDeliveryRequest,
  dispatchPreparedRequest,
  fulfillDeliveryRequest,
  getAvailableCouriersRequest,
  getCompanyWarehousesRequest,
  getDriversRequest,
  getOrderDetailRequest,
  prepareOrderLineItemRequest,
  updateOrderInfoRequest,
  updateOrderStateRequest,
  updateStatusHistoryRequest,
  uploadProofOfDeliveryRequest,
} from './orderDetailsApi';
import { extractApiError } from '../../utils/errors';
import { uploadProgressReporter } from '../feedback/uploadProgress';
import {
  handOverAtPickupPointRequest,
  redirectToPickupPointRequest,
  revertPickupPointRequest,
} from '../pickupPoints/pickupPointsApi';

/**
 * One function, fifteen thunks.
 *
 * It used to be `error.response?.data?.error || ...`, which returns whatever is
 * under that key **unflattened** — and DRF files a serializer's validation
 * errors there as an object. That object reached `{error}` in `OrderDetails`
 * and React threw "Objects are not valid as a React child": a white screen with
 * no way back. `extractApiError` always returns a string.
 */
const messageFrom = (error, fallback) => extractApiError(error, fallback);

export const fetchOrderDetails = createAsyncThunk(
  'orderDetails/fetchOrderDetails',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await getOrderDetailRequest(orderId);
      return response.data.order;
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Error al cargar los detalles de la orden'));
    }
  }
);

/**
 * Couriers the company may route through.
 *
 * Fetched once per visit to the detail and kept in the slice, so reopening the
 * courier selector does not hit the network again.
 */
export const fetchAvailableCouriers = createAsyncThunk(
  'orderDetails/fetchAvailableCouriers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAvailableCouriersRequest();
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Error al cargar los couriers disponibles'));
    }
  },
  {
    condition: (_, { getState }) => !getState().orderDetails.couriersLoaded,
  }
);

/**
 * Warehouses the order can be dispatched from.
 *
 * Keyed by the seller's company, not by the signed-in admin: an admin editing
 * a seller's order must pick from that seller's warehouses.
 */
export const fetchOrderWarehouses = createAsyncThunk(
  'orderDetails/fetchOrderWarehouses',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await getCompanyWarehousesRequest(companyId);
      const warehouses = response.data?.serialized_warehouses || [];
      return warehouses.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
      }));
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Error al cargar las bodegas'));
    }
  }
);

/**
 * Drivers available to dispatch with, on the `admin_checkout` plan.
 *
 * @param {{adminId?: number|string}} [args] - Whose drivers. Only the internal
 *   team may ask for somebody else's; for anybody else the backend ignores it
 *   and answers with their own. Asking for a different operator re-fetches:
 *   the cached list belongs to whoever was being looked at before.
 */
export const fetchDrivers = createAsyncThunk(
  'orderDetails/fetchDrivers',
  async ({ adminId } = {}, { rejectWithValue }) => {
    try {
      const response = await getDriversRequest(adminId);
      const drivers = response.data?.drivers || [];
      return drivers.map((driver) => ({
        value: driver.id,
        label: driver.full_name,
      }));
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Error al cargar los conductores'));
    }
  },
  {
    condition: ({ adminId } = {}, { getState }) =>
      Boolean(adminId) || !getState().orderDetails.driversLoaded,
  }
);

/**
 * Rewrites the shipping block of the order.
 *
 * The address travels as `billing_address` — the backend has always named it
 * that way even though it is the delivery address — and Mexican orders add the
 * colonia and postal code that Chilean ones do not have.
 *
 * @param {{orderId: number|string, values: object, countryId: number|string, isMexico: boolean}} args
 */
export const updateOrderShipping = createAsyncThunk(
  'orderDetails/updateOrderShipping',
  async ({ orderId, values, countryId, isMexico }, { dispatch, rejectWithValue }) => {
    const address = {
      country: countryId,
      street: values.street,
      street_number: values.streetNumber,
      commune: values.commune,
      region: values.region,
    };

    if (isMexico) {
      address.settlement = values.settlement;
      address.postal_code = values.postalCode;
    }

    try {
      await updateOrderInfoRequest({
        action: 'shipping',
        id: orderId,
        shipping_method: values.shippingMethod,
        warehouse: values.warehouseId,
        notes: values.notes,
        dispatch_guide_number: values.dispatchGuide || '',
        packages: values.packages,
        weight: values.weight,
        billing_address: address,
        delivery: { driver_id: values.driverId || null },
      });
      await dispatch(fetchOrderDetails(orderId));
      return true;
    } catch (error) {
      return rejectWithValue(
        messageFrom(error, 'Ocurrió un error al actualizar la información de la orden')
      );
    }
  }
);

/** Rewrites the contact block of the order. */
export const updateOrderRecipient = createAsyncThunk(
  'orderDetails/updateOrderRecipient',
  async ({ orderId, values }, { dispatch, rejectWithValue }) => {
    try {
      await updateOrderInfoRequest({
        action: 'recipient',
        id: orderId,
        recipient: values.name,
        contact_email: values.email,
        contact_phone: values.phone,
        // Always sent, empty included: the backend keeps the stored value when
        // the key is missing, which is what lets an empty field clear it.
        contact_rut: values.taxId || '',
      });
      await dispatch(fetchOrderDetails(orderId));
      return true;
    } catch (error) {
      return rejectWithValue(
        messageFrom(error, 'Ocurrió un error al actualizar el destinatario')
      );
    }
  }
);

/**
 * Sets the status of the order and cascades it to every delivery item.
 *
 * Destructive by nature — the caller is expected to have confirmed with the
 * operator first.
 */
export const updateOrderState = createAsyncThunk(
  'orderDetails/updateOrderState',
  async ({ orderId, status, substatus, details, imageUrls }, { dispatch, rejectWithValue }) => {
    try {
      await updateOrderStateRequest(orderId, {
        status,
        substatus,
        details,
        image_urls: imageUrls || [],
      });
      await dispatch(fetchOrderDetails(orderId));
      return true;
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Ocurrió un error al actualizar el estado'));
    }
  }
);

/** Edits the free-text detail of one event already in the history. */
export const updateStatusHistoryDetail = createAsyncThunk(
  'orderDetails/updateStatusHistoryDetail',
  async ({ orderId, deliveryId, statusId, details }, { dispatch, rejectWithValue }) => {
    try {
      await updateStatusHistoryRequest(deliveryId, statusId, details);
      await dispatch(fetchOrderDetails(orderId));
      return true;
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Ocurrió un error al actualizar el detalle'));
    }
  }
);

/** Marks the delivery as prepared. */
export const fulfillOrder = createAsyncThunk(
  'orderDetails/fulfillOrder',
  async ({ orderId, deliveryId }, { dispatch, rejectWithValue }) => {
    try {
      await fulfillDeliveryRequest(deliveryId);
      await dispatch(fetchOrderDetails(orderId));
      return true;
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Ocurrió un error al preparar el envío'));
    }
  }
);

/** Forces tracking creation and dispatch. */
export const dispatchPreparedOrder = createAsyncThunk(
  'orderDetails/dispatchPreparedOrder',
  async ({ orderId, deliveryId }, { dispatch, rejectWithValue }) => {
    try {
      await dispatchPreparedRequest(deliveryId);
      await dispatch(fetchOrderDetails(orderId));
      return true;
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Ocurrió un error al despachar el envío'));
    }
  }
);

/**
 * Reassigns the courier.
 *
 * Resolves to `{ needsDispatchConfirmation: true }` when the courier has no
 * stand-by window: the backend has accepted the change but the parcel will
 * leave immediately, and the operator has to be told before the UI moves on.
 */
export const changeOrderCourier = createAsyncThunk(
  'orderDetails/changeOrderCourier',
  async ({ orderId, deliveryId, courier }, { dispatch, rejectWithValue }) => {
    try {
      const response = await changeCourierRequest(deliveryId, courier);
      const needsDispatchConfirmation = response.data?.status === 'dispatching';
      await dispatch(fetchOrderDetails(orderId));
      return { courier, needsDispatchConfirmation };
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Ocurrió un error al cambiar el courier'));
    }
  }
);

/*
 * A shipment and its pickup point. Each one refetches the order: the backend
 * rewrites the shipping address (a copy of the point's) and the status, and the
 * detail redraws from the order as the server left it.
 */

/** Sends the shipment to one of the operator's pickup points instead of the customer's door. */
export const redirectToPickupPoint = createAsyncThunk(
  'orderDetails/redirectToPickupPoint',
  async ({ orderId, deliveryId, pickupPointId }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await redirectToPickupPointRequest(deliveryId, pickupPointId);
      await dispatch(fetchOrderDetails(orderId));
      return data.delivery;
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'No se pudo cambiar el destino del envío'));
    }
  }
);

/** Back to the customer's original address. */
export const revertPickupPoint = createAsyncThunk(
  'orderDetails/revertPickupPoint',
  async ({ orderId, deliveryId }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await revertPickupPointRequest(deliveryId);
      await dispatch(fetchOrderDetails(orderId));
      return data.delivery;
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'No se pudo volver al domicilio del cliente'));
    }
  }
);

/** The pickup point handed the parcel to the end customer: the shipment is delivered. */
export const handOverAtPickupPoint = createAsyncThunk(
  'orderDetails/handOverAtPickupPoint',
  async ({ orderId, deliveryId, receivedBy }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await handOverAtPickupPointRequest(deliveryId, receivedBy || null);
      await dispatch(fetchOrderDetails(orderId));
      return data.delivery;
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'No se pudo registrar la entrega al cliente'));
    }
  }
);

/** Registers how many units of one order line have been picked. */
export const prepareOrderLineItem = createAsyncThunk(
  'orderDetails/prepareOrderLineItem',
  async ({ orderId, orderProductId, quantity }, { dispatch, rejectWithValue }) => {
    try {
      await prepareOrderLineItemRequest(orderProductId, quantity);
      await dispatch(fetchOrderDetails(orderId));
      return orderProductId;
    } catch (error) {
      return rejectWithValue(
        messageFrom(error, 'Ocurrió un error al registrar la preparación del producto')
      );
    }
  },
  // Keyed by the line, so only its button shows the count on its way.
  { getPendingMeta: ({ arg }) => ({ pendingKey: arg.orderProductId }) }
);

/**
 * Uploads proof-of-delivery images.
 *
 * Names each file after the order and the current timestamp so two operators
 * uploading the same camera roll cannot overwrite each other.
 */
export const uploadProofOfDelivery = createAsyncThunk(
  'orderDetails/uploadProofOfDelivery',
  async ({ orderId, files }, { dispatch, requestId, rejectWithValue }) => {
    try {
      const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, '');
      const report = uploadProgressReporter(dispatch, requestId, 'Subiendo las imágenes');
      const uploads = files.map((file, index) => {
        const extension = file.name.split('.').pop();
        const base = file.name.split('.')[0];
        const filename = `${orderId}-${base}-${timestamp}${index}.${extension}`;
        return uploadProofOfDeliveryRequest(file, filename, report(index));
      });

      const responses = await Promise.all(uploads);
      return responses.map((response) => response.data.file_url).filter(Boolean);
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Ocurrió un error al subir las imágenes'));
    }
  }
);

export const deleteProofOfDelivery = createAsyncThunk(
  'orderDetails/deleteProofOfDelivery',
  async (url, { rejectWithValue }) => {
    try {
      await deleteProofOfDeliveryRequest(url);
      return url;
    } catch (error) {
      return rejectWithValue(messageFrom(error, 'Ocurrió un error al eliminar la imagen'));
    }
  }
);
